import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import { formatSyllabusDate } from 'routes/Portfolio/Component/CoursesTab/Topic/components/utils/dateHelper';

export default function DescriptionModal({
  show,
  onClose,
  value,
  onSave,
  title = 'Edit Description',
  lectureTopic = '',
  lectureDate = '',
  readingsScheduleDate = '',
}) {
  const [description, setDescription] = useState(value || '');
  const [topic, setTopic] = useState(lectureTopic || '');
  const [date, setDate] = useState(lectureDate || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const validateFields = () => {
    if (!date) {
      return 'Lecture date is required.';
    }

    if (!topic || topic.trim() === '') {
      return 'Lecture topic is required.';
    }

    return '';
  };

  useEffect(() => {
    setDescription(value || '');
    setTopic(lectureTopic || '');
    setDate(lectureDate || '');
    setError('');
    setSaving(false);
  }, [value, show, lectureTopic, lectureDate]);

  const handleSave = async () => {
    const validationMessage = validateFields();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setSaving(true);
      setError('');
      await onSave({
        description,
        topic: topic.trim(),
        date,
      });
      onClose();
    } catch (saveError) {
      setError('Failed to save description. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getReadingsScheduleDate = () => {
    if (!readingsScheduleDate) {
      return '';
    }

    return formatSyllabusDate(readingsScheduleDate, 'dddd, MMM Do');
  };

  return (
    <Modal show={show} onHide={onClose} className="syllabus-description-modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="syllabus-description-modal-fields">
          {/* Lecture topic - editable */}
          <div className="syllabus-description-modal-field">
            <label className="syllabus-description-modal-field-label">
              Lecture topic
            </label>
            <input
              type="text"
              className="syllabus-description-modal-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter lecture topic"
            />
          </div>

          {/* Lecture date - editable */}
          <div className="syllabus-description-modal-field">
            <label className="syllabus-description-modal-field-label">
              Lecture date
            </label>
            <input
              type="date"
              className="syllabus-description-modal-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Readings schedule info - read only */}
          <div className="syllabus-description-modal-field">
            <div className="syllabus-description-modal-info">
              Readings will be generated on {getReadingsScheduleDate()}
            </div>
          </div>

          {/* Description - editable */}
          <div className="syllabus-description-modal-field">
            <label className="syllabus-description-modal-field-label">
              Description
            </label>
            <textarea
              className="syllabus-description-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              rows={12}
            />
          </div>
        </div>

        <div className="syllabus-description-modal-meta">
          {error ? (
            <div className="syllabus-description-modal-error">{error}</div>
          ) : (
            <div />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="modal-gap">
        <RectangularButton onClick={onClose} disabled={saving}>
          Cancel
        </RectangularButton>
        <RectangularButton darkButton onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </RectangularButton>
      </Modal.Footer>
    </Modal>
  );
}
